import { GetStaticPaths, GetStaticProps } from 'next'
import { serverSideTranslations } from 'next-i18next/pages/serverSideTranslations'
import { ReactElement } from 'react'

import type { CampaignPublic, WorldMapShapes } from '@core/journeys/ui/Campaign'
import { getJourneyRTL } from '@core/journeys/ui/rtl'
import { GET_JOURNEY } from '@core/journeys/ui/useJourneyQuery'

import { GetJourney, GetJourneyVariables } from '../../__generated__/GetJourney'
import {
  GetJourneysSummary,
  GetJourneysSummaryVariables
} from '../../__generated__/GetJourneysSummary'
import { CampaignPageKind, IdType } from '../../__generated__/globalTypes'
import i18nConfig from '../../next-i18next.config'
import { CampaignPageWrapper } from '../../src/components/CampaignPageWrapper'
import { createApolloClient } from '../../src/libs/apolloClient'
import { getHostCampaignStaticProps } from '../../src/libs/campaignStaticProps'
import { getFlags } from '../../src/libs/getFlags'
import { JOURNEY_STATUS_EXCLUDE_DRAFT } from '../../src/libs/journeyQueryOptions'
import JourneysPage, { GET_JOURNEYS } from '../home'

import { HostJourneyPage } from './[journeySlug]'

interface HostJourneysPageProps {
  host?: string
  journeys?: GetJourneysSummary['journeys']
  journey?: GetJourney['journey']
  campaign?: CampaignPublic
  worldMap?: WorldMapShapes | null
}

function HostJourneysPage({
  journeys,
  journey,
  campaign,
  worldMap,
  host
}: HostJourneysPageProps): ReactElement {
  if (campaign != null)
    return (
      <CampaignPageWrapper
        campaign={campaign}
        pageKind={CampaignPageKind.landing}
        worldMap={worldMap}
        basePath=""
      />
    )

  if (journey == null) return <JourneysPage journeys={journeys ?? []} />

  const { rtl, locale } = getJourneyRTL(journey)
  return (
    <HostJourneyPage
      journey={journey}
      host={host ?? ''}
      locale={locale}
      rtl={rtl}
    />
  )
}

export const getStaticProps: GetStaticProps<HostJourneysPageProps> = async (
  context
) => {
  // Campaign Domain first: its published campaign owns `/`, else the
  // collection logic below runs unchanged.
  const campaignProps = await getHostCampaignStaticProps({
    hostname: context.params?.hostname as string,
    locale: context.locale
  })
  if (campaignProps != null) return campaignProps

  const apolloClient = createApolloClient()
  const { data } = await apolloClient.query<
    GetJourneysSummary,
    GetJourneysSummaryVariables
  >({
    query: GET_JOURNEYS,
    variables: {
      options: {
        hostname: context.params?.hostname as string,
        journeyCollection: true
      }
    }
  })
  // Apollo Client 4 types `data` as optional. The default `none` error
  // policy rejects rather than resolving without data, so this narrows
  // the type rather than guarding a reachable branch.
  if (data == null) throw new Error('GetJourneysSummary returned no data')

  if (data.journeys === null) {
    return {
      props: {
        ...(await serverSideTranslations(
          context.locale ?? 'en',
          ['apps-journeys', 'libs-journeys-ui'],
          i18nConfig
        ))
      },
      notFound: true,
      revalidate: 1
    }
  } else if (data.journeys.length === 1) {
    try {
      const { data: journeyData } = await apolloClient.query<
        GetJourney,
        GetJourneyVariables
      >({
        query: GET_JOURNEY,
        variables: {
          id: data.journeys[0].slug.toString(),
          idType: IdType.slug,
          options: {
            hostname: context.params?.hostname?.toString() ?? '',
            status: JOURNEY_STATUS_EXCLUDE_DRAFT
          }
        }
      })
      // Apollo Client 4 types `data` as optional. The default `none` error
      // policy rejects rather than resolving without data, so this narrows
      // the type rather than guarding a reachable branch.
      if (journeyData == null) throw new Error('GetJourney returned no data')

      return {
        props: {
          flags: await getFlags(),
          host: context.params?.host?.toString() ?? '',
          ...(await serverSideTranslations(
            context.locale ?? 'en',
            ['apps-journeys', 'libs-journeys-ui'],
            i18nConfig
          )),
          journey: journeyData.journey,
          journeys: []
        },
        revalidate: 60
      }
    } catch {
      return {
        props: {
          ...(await serverSideTranslations(
            context.locale ?? 'en',
            ['apps-journeys', 'libs-journeys-ui'],
            i18nConfig
          ))
        },
        notFound: true,
        revalidate: 1
      }
    }
  }
  return {
    props: {
      flags: await getFlags(),
      ...(await serverSideTranslations(
        context.locale ?? 'en',
        ['apps-journeys', 'libs-journeys-ui'],
        i18nConfig
      )),
      journeys: data.journeys
    },
    revalidate: 60
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  }
}

export default HostJourneysPage
